import { useState, type FormEvent } from "react";
import { api, errorMessage, isCode } from "../api/client.ts";
import type { Session } from "../api/types.ts";
import { CodeInput } from "../components/CodeInput.tsx";
import { IconDevice, IconPhone } from "../components/Icons.tsx";
import { StepUpDialog } from "../components/StepUp.tsx";
import { TelegramCard } from "../components/Telegram.tsx";
import { useToast } from "../components/Toast.tsx";
import { Alert, PageHeader, Spinner } from "../components/ui.tsx";
import { formatTimeFull, timeAgo } from "../lib/format.ts";
import { useAsync } from "../lib/hooks.ts";
import { useMe } from "../lib/session.tsx";
import { fmt, rich, tr, useT } from "../i18n/index.tsx";
import { roleName } from "./Roles.tsx";

/** "Chrome on Windows" from a user agent string (good enough for a session list). */
function describeUa(ua: string | null): { text: string; mobile: boolean } {
  const t = tr().security;
  if (!ua) return { text: t.unknownDevice, mobile: false };
  const os = /iPhone|iPad/.test(ua) ? "iOS" : /Android/.test(ua) ? "Android" : /Mac OS X/.test(ua) ? "macOS" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : t.unknownOs;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : t.browser;
  return { text: fmt(t.browserOnOs, { browser, os }), mobile: /Mobile|iPhone|Android/.test(ua) };
}

export function Security() {
  const t = useT();
  const me = useMe();
  return (
    <div className="page narrow">
      <PageHeader title={t.security.title} eyebrow={t.security.eyebrow}>{t.security.intro}</PageHeader>
      <section className="card section-block account-card">
        <div className="account-grid">
          <div><span>{t.security.name}</span><strong>{me.user.name}</strong></div>
          <div><span>{t.security.email}</span><strong>{me.user.email}</strong></div>
          <div><span>{t.security.role}</span><strong>{roleName(me.role)} · {me.merchant.name}</strong></div>
          <div><span>{t.security.organization}</span><strong>{me.organization.name}</strong></div>
          <div><span>{t.security.twoStep}</span><strong className="ok-text">{t.security.on}</strong></div>
        </div>
      </section>
      <TelegramCard />
      <Sessions />
      <ChangePassword />
    </div>
  );
}

function Sessions() {
  const t = useT();
  const q = useAsync(() => api.get<{ items: Session[] }>("/security/sessions"), []);
  const [revoke, setRevoke] = useState<Session | null>(null);
  const toast = useToast();
  const items = q.data?.items ?? [];

  return (
    <section className="card section-block" aria-labelledby="sess-h">
      <div className="card-head">
        <div>
          <h2 id="sess-h" className="card-title">{t.security.sessionsTitle}</h2>
          <p className="card-sub">{t.security.sessionsSub}</p>
        </div>
      </div>
      {q.error ? (
        <Alert tone="error">{errorMessage(q.error)}</Alert>
      ) : q.loading ? (
        <span className="sk sk-lg" />
      ) : (
        <ul className="session-list">
          {items.map((s) => {
            const d = describeUa(s.user_agent);
            return (
              <li key={s.id} className="session">
                <span className="session-icon">{d.mobile ? <IconPhone /> : <IconDevice />}</span>
                <div className="session-text">
                  <strong>{d.text} {s.current && <span className="you-tag">{t.security.thisDevice}</span>}</strong>
                  <span className="muted small">
                    {rich(t.security.sessionMeta, { code: (c) => <span className="mono-sm">{c}</span> }, { ip: s.ip ?? t.security.unknownIpTitle, ago: timeAgo(s.last_seen_at), at: formatTimeFull(s.created_at) })}
                  </span>
                </div>
                {!s.current && <button type="button" className="btn btn-sm btn-ghost btn-danger-ghost" onClick={() => setRevoke(s)}>{t.security.endSession}</button>}
              </li>
            );
          })}
        </ul>
      )}
      <StepUpDialog
        open={!!revoke}
        onClose={() => setRevoke(null)}
        title={t.security.endTitle}
        description={revoke ? fmt(t.security.endDesc, { device: describeUa(revoke.user_agent).text, ip: revoke.ip ?? t.security.unknownIp }) : undefined}
        confirmLabel={t.security.endSession}
        danger
        needsCode={false}
        onConfirm={async () => {
          await api.post(`/security/sessions/${encodeURIComponent(revoke?.id ?? "")}/revoke`);
          toast(t.security.sessionEnded);
          void q.reload();
        }}
      />
    </section>
  );
}

function ChangePassword() {
  const t = useT();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const toast = useToast();
  const pwErr = pw && pw.length < 12 ? t.security.pwTooShort : pw.length > 128 ? t.security.pwTooLong : null;
  const matchErr = pw2 && pw !== pw2 ? t.security.pwMismatch : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setDone(false);
    if (!cur) return setError(t.security.curRequired);
    if (pw.length < 12 || pw.length > 128) return setError(t.security.pwLength);
    if (pw !== pw2) return setError(t.security.newMismatch);
    if (code.length !== 6) return setCodeError(t.security.codeRequired);
    setBusy(true);
    setError(null);
    setCodeError(null);
    try {
      await api.post("/security/password", { current_password: cur, new_password: pw, code });
      setCur("");
      setPw("");
      setPw2("");
      setCode("");
      setDone(true);
      toast(t.security.pwChanged);
    } catch (err) {
      setCode("");
      // The server doesn't say which one was wrong (password or code): show its message as-is.
      if (isCode(err, "mfa_required")) setCodeError(t.security.codeFailed);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card section-block" aria-labelledby="pw-h">
      <h2 id="pw-h" className="card-title">{t.security.pwTitle}</h2>
      <p className="card-sub">{t.security.pwSub}</p>
      <form className="form-stack pw-form" onSubmit={submit} noValidate>
        <input type="text" name="username" autoComplete="username" hidden readOnly />
        <div className="field">
          <label htmlFor="cur-pw">{t.security.curPw}</label>
          <input id="cur-pw" type="password" autoComplete="current-password" value={cur} onChange={(e) => setCur(e.target.value)} />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="new-pw">{t.security.newPw}</label>
            <input id="new-pw" type="password" autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} aria-invalid={!!pwErr} aria-describedby="new-pw-msg" />
            <p id="new-pw-msg" className={pwErr ? "field-error" : "field-hint"}>{pwErr ?? t.security.pwHint}</p>
          </div>
          <div className="field">
            <label htmlFor="new-pw2">{t.security.confirmPw}</label>
            <input id="new-pw2" type="password" autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} aria-invalid={!!matchErr} aria-describedby={matchErr ? "new-pw2-msg" : undefined} />
            {matchErr && <p id="new-pw2-msg" className="field-error">{matchErr}</p>}
          </div>
        </div>
        <CodeInput value={code} onChange={(v) => { setCode(v); setCodeError(null); }} error={codeError} disabled={busy} />
        {error && <Alert tone="error">{error}</Alert>}
        {done && <Alert tone="success">{t.security.pwChangedBody}</Alert>}
        <div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy && <Spinner />} {t.security.changePw}</button>
        </div>
      </form>
    </section>
  );
}
